import { useQuery } from '@apollo/client'
import {
    Box,
    useTheme as MuiUseTheme,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material'
import { PieChart } from '@mui/x-charts'
import {
    GetRosterCandidateStatusGroupCountsQuery,
    ProcessStatusGroup,
} from '__generated__/graphql'
import { GetRosterCandidateStatusGroupCounts } from 'client/graphql/search.gql'
import { useState } from 'react'
import {
    getProcessStatusGroupColor,
    useProcessStatusColorMap,
} from 'recruiter/components/ProcessStatusPill/colorHelperFunction'
import { getPxAsRem } from 'shared/utils'

export interface ProcessStatusPieGraphProps {
    rosterId: string
    groups: ProcessStatusGroup[]
}

const ProcessStatusPieGraph: React.FC<ProcessStatusPieGraphProps> = ({
    rosterId,
    groups,
}) => {
    const [groupCounts, setGroupCounts] = useState<
        GetRosterCandidateStatusGroupCountsQuery['getRosterCandidateStatusGroupCounts']
    >([])
    useQuery(GetRosterCandidateStatusGroupCounts, {
        variables: {
            rosterId,
            summaryGroups: groups,
        },
        skip: !rosterId,
        onCompleted: (data) => {
            if (data.getRosterCandidateStatusGroupCounts) {
                setGroupCounts(data.getRosterCandidateStatusGroupCounts)
            }
        },
        onError: (error) => {
            console.error(
                'Error fetching candidate status group counts:',
                error
            )
        },
    })

    const mappedData = groupCounts.map((item) => {
        return {
            value: item.count,
            label: item.statusSummaryGroup.label,
            summaryGroup: item.statusSummaryGroup.summaryGroup,
            color: getProcessStatusGroupColor(
                item.statusSummaryGroup.summaryGroup
            ),
        }
    })

    const sortPriorities = {
        [ProcessStatusGroup.NoStatus]: 0,
        [ProcessStatusGroup.Mapping]: 1,
        [ProcessStatusGroup.Interest]: 2,
        [ProcessStatusGroup.Outreach]: 3,
        [ProcessStatusGroup.Submitted]: 4,
        [ProcessStatusGroup.InProcess]: 5,
        [ProcessStatusGroup.OutOfProcess]: 6,
    }

    const sortedData = mappedData.sort((a, b) => {
        const priorityA = sortPriorities[a.summaryGroup] || 0
        const priorityB = sortPriorities[b.summaryGroup] || 0
        return priorityA - priorityB
    })

    const muiTheme = MuiUseTheme()
    const isSmScreen = useMediaQuery(muiTheme.breakpoints.down('sm'))
    const colorMatch = useProcessStatusColorMap(
        groupCounts.map((item) => item.statusSummaryGroup.summaryGroup)
    )
    const totalCount = groupCounts.reduce((acc, curr) => acc + curr.count, 0)
    const isLgScreen = useMediaQuery(muiTheme.breakpoints.up('lg'))

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent={isLgScreen ? 'flex-end' : 'flex-start'}
            padding={getPxAsRem(0, 24)}
            display="flex"
            flexGrow={1}
            spacing={0}
            minWidth={getPxAsRem(360)}
        >
            <Box
                position="relative"
                width={200}
                height={200}
                justifyContent="center"
                justifyItems={'center'}
                alignItems="center"
            >
                <PieChart
                    width={250}
                    colors={colorMatch}
                    series={[
                        {
                            highlightScope: {
                                fade: 'global',
                                highlight: 'item',
                            },
                            faded: {
                                innerRadius: 40,
                                additionalRadius: -20,
                                color: 'gray',
                            },
                            highlighted: {
                                additionalRadius: 5,
                            },
                            data: sortedData,
                            innerRadius: '75%',
                        },
                    ]}
                    slotProps={{ legend: { hidden: true } }}
                    viewBox={{
                        x: -47.5,
                        y: 0,
                    }}
                />
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    sx={{ pointerEvents: 'none' }}
                />
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    sx={{ transform: 'translate(-50%, -50%)' }}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    {totalCount > 0 && (
                        <Typography variant="h2">{totalCount}</Typography>
                    )}
                </Box>
            </Box>
            {!isSmScreen && (
                <Stack
                    direction="column"
                    spacing={1}
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="center"
                    paddingLeft={2}
                    minWidth={getPxAsRem(160)}
                >
                    {sortedData.map((item) => (
                        <Stack
                            key={item.label}
                            direction="row"
                            spacing={1}
                            display="flex"
                            flex-shrink={1}
                            alignItems="center"
                        >
                            <Box
                                width={getPxAsRem(16)}
                                height={getPxAsRem(16)}
                                borderRadius="25%"
                                bgcolor={getProcessStatusGroupColor(
                                    item.summaryGroup
                                )}
                                marginRight={1}
                            />
                            <Typography variant="body2">
                                {item.label} ({item.value})
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    )
}

export default ProcessStatusPieGraph
